import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Field } from "@/components/ui-kit";
import { toast } from "sonner";
import { RefreshCw, Send, Check } from "lucide-react";

const SEVERITY_OPTS = [
  { value: "", label: "All severities" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
];

const EMPTY_SUMMARY = { open: 0, open_high: 0, last_24h: 0, alerts_enabled: true, alert_email: null };
const SUMMARY_POLL_MS = 60000;

function pad(n) {
  return String(n).padStart(2, "0");
}

function fmtWhen(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

function fmtTimeOnly(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

export default function Security() {
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [scope, setScope] = useState("open");
  const [severity, setSeverity] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadSummary = useCallback(() => {
    api.get("/security/summary")
      .then((r) => setSummary(r.data || EMPTY_SUMMARY))
      .catch(() => {});
  }, []);

  const loadAlerts = useCallback(() => {
    setLoading(true);
    const params = { include_acknowledged: scope === "all", limit: 200 };
    if (severity) params.severity = severity;
    return api.get("/security/alerts", { params })
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [scope, severity]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    loadSummary();
    const t = setInterval(loadSummary, SUMMARY_POLL_MS);
    return () => clearInterval(t);
  }, [loadSummary]);

  const refresh = () => {
    loadAlerts();
    loadSummary();
  };

  const ack = async (id) => {
    try {
      await api.post(`/security/alerts/${id}/ack`);
      loadAlerts();
      loadSummary();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/security/test-alert");
      toast.success(`Test alert created${data.emailed ? ", and emailed to the security contact" : ""}`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div data-testid="security-page">
      <PageHeader subtitle="Breach detection" title="Security" />

      <div className="text-sm text-slate-600 mb-5 max-w-3xl">
        RideWise watches for the patterns that usually precede a breach: lockouts, sign-ins from new addresses,
        bursts of failed sign-ins, and unusual volumes of downloads or exports. Alerts describe staff accounts and
        IP addresses only, never riders. Acknowledge an alert once you have looked into it.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-md p-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">Open alerts</div>
          <div className="font-display text-xl font-bold text-[#0A192F] mt-1" data-testid="security-summary-open">{summary.open}</div>
        </div>
        <div className={`bg-white border rounded-md p-3 ${summary.open_high > 0 ? "border-rose-300" : "border-slate-200"}`}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">Open high severity</div>
          <div
            className={`font-display text-xl font-bold mt-1 ${summary.open_high > 0 ? "text-rose-700" : "text-[#0A192F]"}`}
            data-testid="security-summary-open-high"
          >
            {summary.open_high}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">Last 24 hours</div>
          <div className="font-display text-xl font-bold text-[#0A192F] mt-1" data-testid="security-summary-24h">{summary.last_24h}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">Security contact</div>
          <div className="text-sm font-medium text-slate-900 mt-1 truncate" data-testid="security-summary-contact">
            {summary.alert_email || <span className="text-slate-400 font-normal">Not set (alerts stay in-app only)</span>}
          </div>
          <Link to="/app/settings" className="text-[11px] text-[#005E82] hover:underline">
            Update in settings
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="security-scope-toggle">
          <button
            onClick={() => setScope("open")}
            className={`text-xs px-3 py-2 ${scope === "open" ? "bg-[#0A192F] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid="security-scope-open"
          >
            Open
          </button>
          <button
            onClick={() => setScope("all")}
            className={`text-xs px-3 py-2 border-l border-slate-200 ${scope === "all" ? "bg-[#0A192F] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
            data-testid="security-scope-all"
          >
            All
          </button>
        </div>
        <div className="w-44">
          <Field testId="security-severity-filter" value={severity} onChange={(e) => setSeverity(e.target.value)} options={SEVERITY_OPTS} />
        </div>
        <div className="flex-1" />
        <button
          onClick={refresh}
          className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400 flex items-center gap-1.5"
          data-testid="security-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
        <button onClick={sendTest} disabled={testing} className="btn-primary flex items-center gap-1.5" data-testid="security-test-alert">
          <Send size={14} /> {testing ? "Sending…" : "Send test alert"}
        </button>
      </div>

      <TableShell
        columns={["When", "Severity", "Alert", "Account", "IP", "Notified", "Action"]}
        empty={!loading && rows.length === 0 ? "No security alerts. That is the goal." : null}
      >
        {rows.map((a) => (
          <tr
            key={a.id}
            className={`tbl-row border-t border-slate-100 ${a.severity === "high" ? "border-l-2 border-l-rose-500" : ""}`}
            data-testid={`security-row-${a.id}`}
          >
            <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">
              <div>{fmtWhen(a.at)}</div>
              {a.occurrences > 1 && (
                <div className="text-slate-400 text-[11px] mt-0.5">x{a.occurrences}, last {fmtTimeOnly(a.last_seen)}</div>
              )}
            </td>
            <td className="px-4 py-2.5">
              <span
                className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                  a.severity === "high" ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200"
                }`}
              >
                {a.severity === "high" ? "High" : "Medium"}
              </span>
            </td>
            <td className="px-4 py-2.5 max-w-[360px]">
              <div className="font-medium text-slate-900">{a.title}</div>
              <div className="text-xs text-slate-600 mt-0.5">{a.summary}</div>
              {a.details && Object.keys(a.details).length > 0 && (
                <div className="text-xs text-slate-500 mt-1 font-mono break-all">
                  {Object.entries(a.details).map(([k, v]) => `${k}: ${v}`).join(", ")}
                </div>
              )}
            </td>
            <td className="px-4 py-2.5 text-xs">{a.user_email || <span className="text-slate-400">n/a</span>}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{a.ip || "n/a"}</td>
            <td className="px-4 py-2.5 text-xs">
              {a.notified_at ? (
                <span title="Emailed to security contact" className="inline-flex items-center text-emerald-700">
                  <Check size={14} />
                </span>
              ) : (
                <span className="text-slate-400">in-app</span>
              )}
            </td>
            <td className="px-4 py-2.5 text-xs">
              {a.acknowledged_at ? (
                <span className="text-slate-500">
                  Acknowledged {fmtWhen(a.acknowledged_at)} by {a.acknowledged_by_email}
                </span>
              ) : (
                <button
                  onClick={() => ack(a.id)}
                  className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:border-slate-400"
                  data-testid={`security-ack-${a.id}`}
                >
                  Acknowledge
                </button>
              )}
            </td>
          </tr>
        ))}
      </TableShell>
    </div>
  );
}
